import { supabase } from '../lib/supabase';
import { handleApiError } from './config';

// 🐾 냥 잔고 조회
export async function getYangBalance(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('yang_balance')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data.yang_balance;
}

// 🐾 냥 거래내역 조회
export async function getYangTransactions(userId, { limit = 20, offset = 0 } = {}) {
  const { data, error } = await supabase
    .from('yang_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return data;
}

// 🐾 냥 충전
export async function chargeYang(userId, amount, { adminId = null, description = '', metadata = {} } = {}) {
  // 1. 현재 잔고 조회
  const balance = await getYangBalance(userId);
  const newBalance = balance + amount;

  // 2. 잔고 업데이트
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ yang_balance: newBalance })
    .eq('id', userId);

  if (updateError) throw updateError;

  // 3. 거래내역 기록
  const { error: txError } = await supabase
    .from('yang_transactions')
    .insert([{
      user_id: userId,
      admin_id: adminId,
      amount: amount,
      balance_after: newBalance,
      type: 'charge',
      status: 'success',
      description,
      metadata,
      created_at: new Date().toISOString(),
    }]);

  if (txError) throw txError;

  return newBalance;
}

// 🐾 냥 결제(잔고 차감)
export async function purchaseYangProduct(userId, amount, { productId = null, description = '', metadata = {} } = {}) {
  // 1. 현재 잔고 조회
  const balance = await getYangBalance(userId);
  if (balance < amount) throw new Error('잔고 부족');

  const newBalance = balance - amount;

  // 2. 잔고 업데이트
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ yang_balance: newBalance })
    .eq('id', userId);

  if (updateError) throw updateError;

  // 3. 거래내역 기록
  const { error: txError } = await supabase
    .from('yang_transactions')
    .insert([{
      user_id: userId,
      amount: -amount,
      balance_after: newBalance,
      type: 'purchase',
      status: 'success',
      description: description || `상품구매: ${productId}`,
      metadata: { ...metadata, product_id: productId },
      created_at: new Date().toISOString(),
    }]);

  if (txError) throw txError;

  return newBalance;
}

// 🐾 냥 환불
export async function refundYang(userId, amount, { description = '', metadata = {} } = {}) {
  // 1. 현재 잔고 조회
  const balance = await getYangBalance(userId);
  const newBalance = balance + amount;

  // 2. 잔고 업데이트
  const { error: updateError } = await supabase
    .from('profiles')
    .update({ yang_balance: newBalance })
    .eq('id', userId);

  if (updateError) throw updateError;

  // 3. 거래내역 기록
  const { error: txError } = await supabase
    .from('yang_transactions')
    .insert([{
      user_id: userId,
      amount: amount,
      balance_after: newBalance,
      type: 'refund',
      status: 'success',
      description,
      metadata,
      created_at: new Date().toISOString(),
    }]);

  if (txError) throw txError;

  return newBalance;
}

// === 상품 지급 관련 함수 ===

// 상품 정보 조회 (yang_products 테이블에서)
export async function getProductInfo(productId) {
  const { data, error } = await supabase
    .from('yang_products')
    .select('*')
    .eq('product_code', productId)
    .single();
  if (error) throw error;
  return data;
}

// 정기권 지급 (만료일 갱신)
export async function grantSubscription(userId, months) {
  // profiles.subscription_expiry 컬럼이 있다고 가정
  const { data, error } = await supabase
    .from('profiles')
    .select('subscription_expiry')
    .eq('id', userId)
    .single();
  if (error) throw error;

  const now = new Date();
  let baseDate = data.subscription_expiry && new Date(data.subscription_expiry) > now
    ? new Date(data.subscription_expiry)
    : now;
  baseDate.setMonth(baseDate.getMonth() + months);

  const { error: updateError } = await supabase
    .from('profiles')
    .update({ subscription_expiry: baseDate.toISOString() })
    .eq('id', userId);
  if (updateError) throw updateError;

  return baseDate;
}

// 권종별 문제 수 매핑
const ticketProblemCount = {
  'MPT_AI_2': 2,
  'MPT_PROFESSOR_2': 2,
  'MEE_AI_6': 6,
  'MEE_PROFESSOR_6': 6,
  // 필요시 추가
};

// 응시권 지급 (remain 자동 반영)
export async function grantExamTicket(userId, type, count) {
  const problemCount = ticketProblemCount[type] || 1; // 기본값 1문제
  const remainToAdd = count * problemCount;

  // 기존 데이터 조회
  const { data: existing } = await supabase
    .from('user_exam_tickets')
    .select('count, remain')
    .eq('user_id', userId)
    .eq('type', type)
    .single();

  let newCount = count;
  let newRemain = remainToAdd;
  if (existing) {
    newCount += existing.count;
    newRemain += existing.remain;
  }

  const { error } = await supabase
    .from('user_exam_tickets')
    .upsert([
      { user_id: userId, type, count: newCount, remain: newRemain },
    ], { onConflict: ['user_id', 'type'], ignoreDuplicates: false });
  if (error) throw error;
  return true;
}

// 스터디권 지급 (user_study_tickets 테이블에 수량 증가, 실제 테이블/컬럼명은 프로젝트에 맞게 수정)
export async function grantStudyTicket(userId, type, count) {
  // type이 반드시 product_code여야 함!
  const { error } = await supabase
    .from('user_study_tickets')
    .upsert([
      { user_id: userId, type, count },
    ], { onConflict: ['user_id', 'type'], ignoreDuplicates: false });
  if (error) throw error;
  return true;
}

// 패키지 지급 (구성 상품 반복 지급)
export async function grantPackage(userId, product) {
  // product.features 또는 product.metadata에 구성 정보가 있다고 가정
  // 실제 지급 로직은 프로젝트 정책에 맞게 구현 필요
  // 예시: features 배열에서 각 상품명/타입을 파싱하여 지급
  // 아래는 예시 코드이며, 실제로는 구성 정보 파싱이 필요함
  return true;
}

// BLL 학습권/정기권 지급
export async function grantBllPass(userId, type, months) {
  console.log('[grantBllPass] 호출됨:', { userId, type, months });
  const { data: existing, error: fetchError } = await supabase
    .from('user_bll_passes')
    .select('*')
    .eq('user_id', userId)
    .eq('type', type)
    .single();

  if (fetchError) {
    console.error('[grantBllPass] select error:', fetchError);
  }

  const now = new Date();
  let newExpireAt;
  if (existing && existing.expire_at && new Date(existing.expire_at) > now) {
    newExpireAt = new Date(existing.expire_at);
    newExpireAt.setMonth(newExpireAt.getMonth() + months);
  } else {
    newExpireAt = new Date();
    newExpireAt.setMonth(newExpireAt.getMonth() + months);
  }

  if (existing) {
    const { error } = await supabase
      .from('user_bll_passes')
      .update({
        count: 1,
        expire_at: newExpireAt.toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id);
    if (error) {
      console.error('[grantBllPass] update error:', error);
      throw error;
    } else {
      console.log('[grantBllPass] update 성공');
    }
  } else {
    const { error } = await supabase
      .from('user_bll_passes')
      .insert([
        {
          user_id: userId,
          type,
          count: 1,
          expire_at: newExpireAt.toISOString(),
        }
      ]);
    if (error) {
      console.error('[grantBllPass] insert error:', error);
      throw error;
    } else {
      console.log('[grantBllPass] insert 성공');
    }
  }
  // BLL PASS 지급 후, profiles.subscription_expiry도 갱신
  const { error: profileUpdateError } = await supabase
    .from('profiles')
    .update({ subscription_expiry: newExpireAt.toISOString() })
    .eq('id', userId);
  if (profileUpdateError) {
    console.error('[grantBllPass] profiles.subscription_expiry update error:', profileUpdateError);
    throw profileUpdateError;
  }
  return true;
}

// 상품별 결제/지급 통합 함수
export async function purchaseAndGrantProduct(userId, productId) {
  // 1. 상품 정보 조회
  const product = await getProductInfo(productId);

  // 2. 결제(잔고 차감)
  await purchaseYangProduct(userId, product.price, {
    productId: product.product_code,
    description: product.name
  });

  // 3. 상품별 지급
  if (['BLL_MONTHLY', 'BLL_6MONTH'].includes(product.product_code)) {
    console.log('[purchaseAndGrantProduct] BLL PASS 지급 분기 진입:', product.product_code, userId);
    let months = product.product_code === 'BLL_6MONTH' ? 6 : 1;
    await grantBllPass(userId, product.product_code, months);
  } else if (product.category === 'subscription') {
    // 기존 정기권 지급 로직 (다른 상품)
    let months = 1;
    if (product.period && product.period.includes('6')) months = 6;
    await grantSubscription(userId, months);
  } else if (product.category === 'mee' || product.category === 'mpt') {
    await grantExamTicket(userId, product.product_code, 1);
  } else if (product.category === 'study') {
    await grantStudyTicket(userId, product.product_code, 1);
  } else if (product.category === 'bundle') {
    await grantPackage(userId, product);
  }
  // 4. 결과 반환
  return { success: true, product: product.name };
}
